import { useCallback, useRef, useState } from 'react';

/**
 * Phase 36.5 — useSequentialPulse
 * 
 * Manages the n8n-style sequential execution pulse animation.
 * When an execution path arrives from the SSE broker, this hook
 * drives the visual state step-by-step:
 * 
 *   Step 0: Trigger node glows
 *   Step 1: Edge 0 animates → Core node glows  
 *   Step 2: Edge 1 animates → Action node glows
 *   ...
 *   Final: Everything clears
 * 
 * The `currentNodeId` drives node glow styling.
 * The `currentEdgeId` drives edge animation.
 * The `visitedNodeIds` array creates a "trail" effect — previously
 * visited nodes keep a subtle glow as the pulse progresses.
 */

export interface PulseStep {
  nodeId: string;
  nodeType: string;
}

export interface PulseEdge {
  edgeId: string;
  source: string;
  target: string;
}

export interface PulseState {
  /** The node currently being "executed" — gets the heavy neon glow */
  currentNodeId: string | null;
  /** The edge currently being traversed — gets the animated particle */
  currentEdgeId: string | null;
  /** All nodes that have been visited so far — get subtle trail glow */
  visitedNodeIds: string[];
  /** All edges that have been traversed — get subtle trail glow */
  visitedEdgeIds: string[];
  /** Whether a pulse sequence is actively playing */
  isPulsing: boolean;
}

const STEP_DURATION_MS = 220;      // Snappy, energetic node glow (<2.5s for 10-step full flow)
const EDGE_DURATION_MS = 90;        // Fast animated edge particle traversal
const TRAIL_FADE_MS = 2500;        // How long the trail persists after sequence ends

const INITIAL_STATE: PulseState = {
  currentNodeId: null,
  currentEdgeId: null,
  visitedNodeIds: [],
  visitedEdgeIds: [],
  isPulsing: false,
};

export function useSequentialPulse() {
  const [pulseState, setPulseState] = useState<PulseState>(INITIAL_STATE);
  const timersRef = useRef<NodeJS.Timeout[]>([]);
  const isPlayingRef = useRef(false);

  /** Cancel any in-progress animation sequence */
  const cancelSequence = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
    isPlayingRef.current = false;
  }, []);

  /**
   * Play a full execution path sequence.
   * Steps and edges are processed in order with staggered timing.
   */
  const playSequence = useCallback((steps: PulseStep[], edges: PulseEdge[]) => {
    // Cancel any existing timers
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];

    if (!steps || steps.length === 0) {
      isPlayingRef.current = false;
      setPulseState(INITIAL_STATE);
      return;
    }

    isPlayingRef.current = true;
    const newTimers: NodeJS.Timeout[] = [];
    // Preserve existing visited trail during sub-steps, but start fresh on new turns
    const isNewTurnIntake = steps.length === 1 || (steps[0] && (steps[0].nodeType || '').toLowerCase().includes('whatsapp'));
    const visitedNodes: string[] = isNewTurnIntake ? [] : [...(pulseState.visitedNodeIds || [])];
    const visitedEdges: string[] = isNewTurnIntake ? [] : [...(pulseState.visitedEdgeIds || [])];

    // Identify central core node ID for star-topology edge matching
    const coreStep = steps.find(s => 
      (s.nodeType || '').toLowerCase().includes('core') || 
      (s.nodeId || '').toLowerCase().includes('core')
    );
    const coreNodeId = coreStep ? coreStep.nodeId : null;

    let accumulatedDelay = 0;

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      let edge: PulseEdge | null = null;

      if (i > 0) {
        const prevStep = steps[i - 1];
        // 1. Direct edge connecting previous node and current node
        const direct = edges.find(e => 
          (e.source === prevStep.nodeId && e.target === step.nodeId) ||
          (e.source === step.nodeId && e.target === prevStep.nodeId)
        );
        if (direct) {
          edge = direct;
        } else if (coreNodeId && step.nodeId !== coreNodeId) {
          // 2. Upstream context edge connecting this node into Luna Core Hub
          const toCore = edges.find(e =>
            (e.source === step.nodeId && e.target === coreNodeId) ||
            (e.source === coreNodeId && e.target === step.nodeId)
          );
          if (toCore) {
            edge = toCore;
          }
        } else if (coreNodeId && prevStep.nodeId === coreNodeId) {
          // 3. Downstream edge connecting Luna Core Hub to current action/output node
          const fromCore = edges.find(e =>
            (e.source === coreNodeId && e.target === step.nodeId) ||
            (e.source === step.nodeId && e.target === coreNodeId)
          );
          if (fromCore) {
            edge = fromCore;
          }
        }

        // 4. Fallback: any edge touching current node
        if (!edge) {
          edge = edges.find(e => e.source === step.nodeId || e.target === step.nodeId) || null;
        }
        // 5. Index fallback
        if (!edge && edges[i - 1]) {
          edge = edges[i - 1];
        }
      }

      // Schedule: animate edge first (if not the first step), then light up node
      if (edge) {
        const edgeDelay = accumulatedDelay;
        const targetEdge = edge;
        newTimers.push(setTimeout(() => {
          if (!isPlayingRef.current) return;
          setPulseState(prev => ({
            ...prev,
            currentEdgeId: targetEdge.edgeId,
            currentNodeId: null, // Dim node during edge transition
            isPulsing: true,
          }));
        }, edgeDelay));
        accumulatedDelay += EDGE_DURATION_MS;
      }

      // Schedule: light up the node
      const nodeDelay = accumulatedDelay;
      const capturedVisitedNodes = [...visitedNodes];
      const capturedVisitedEdges = [...visitedEdges];

      newTimers.push(setTimeout(() => {
        if (!isPlayingRef.current) return;
        setPulseState({
          currentNodeId: step.nodeId,
          currentEdgeId: null, // Edge animation done, node is now active
          visitedNodeIds: [...capturedVisitedNodes],
          visitedEdgeIds: [...capturedVisitedEdges],
          isPulsing: true,
        });
      }, nodeDelay));

      // Track visited for trail effect
      visitedNodes.push(step.nodeId);
      if (edge) visitedEdges.push(edge.edgeId);

      accumulatedDelay += STEP_DURATION_MS;
    }

    // Schedule: clear everything after the sequence
    const lastStep = steps[steps.length - 1];
    const isThinkingEndingOnCore = Boolean(lastStep && coreNodeId && lastStep.nodeId === coreNodeId);

    if (isThinkingEndingOnCore) {
      // 🧠 Thinking Phase: Luna Core Hub is actively synthesizing the response.
      // Keep Luna Core Hub actively glowing (pulse-glow-active) until response arrives or clear signal!
      const thinkingWatchdogDelay = accumulatedDelay + 15000;
      newTimers.push(setTimeout(() => {
        isPlayingRef.current = false;
        setPulseState(INITIAL_STATE);
      }, thinkingWatchdogDelay));
    } else {
      // Response Phase / Output Phase: sequence has reached output/trigger node
      const clearDelay = accumulatedDelay + TRAIL_FADE_MS;
      newTimers.push(setTimeout(() => {
        isPlayingRef.current = false;
        setPulseState(INITIAL_STATE);
      }, clearDelay));
    }

    timersRef.current = newTimers;
  }, [cancelSequence]);

  /**
   * Instant clear — stops animation and resets state.
   */
  const clearPulse = useCallback(() => {
    cancelSequence();
    setPulseState(INITIAL_STATE);
  }, [cancelSequence]);

  return {
    pulseState,
    playSequence,
    clearPulse,
  };
}
